/**
 * Shared constants extracted from game.js.
 * Magic numbers, timing values, configuration objects.
 */

/** Base URL for all API calls. */
export const API = "/api";

/**
 * Hint delay per pending-action type (milliseconds).
 *
 * These delays are intentionally long in normal hints mode: the player should think first
 * and only get guidance after stalling. This is especially important for learners/kids —
 * instant answers train passive waiting. "Learning Mode" is the explicit opt-in that drops
 * delays to zero for players who want constant guidance.
 *
 * Think-time only. These numbers used to carry a second, unnamed wait — "let the scene settle
 * first", which 6-16s dwarfed for free and learning mode's zero delay exposed (the hint painting
 * over flying cards). That wait is structural, so it is no longer a delay at all: hints.js#scheduleHint
 * awaits animate.js#whenSceneSettled before starting the timer below, in every mode.
 */
export const HINT_DELAYS = {
  AWAIT_BID_RESPONSE: 6000,
  AWAIT_FIRST_REFUSAL: 6000,
  AWAIT_HAND_DECISION: 10000,
  AWAIT_DISCARD: 16000,
  AWAIT_GAME_TYPE: 10000,
  AWAIT_CARD_PLAY: 10000,
  // Auto-play/spectator commentary only (PR #78) — hints.js#scheduleHint skips scheduling for
  // AWAIT_AI_CARD entirely when auto-play is off, so this zero delay is never read in normal play.
  AWAIT_AI_CARD: 0,
};

/** Bid number fade-out duration on pass/resolution (milliseconds). */
export const BID_FADE_DURATION = 300;

/** Interval between queued bid animations (milliseconds). */
export const BID_QUEUE_INTERVAL = 600;

// play-test: minimum visual feedback duration for a prompt tile press — ensures the player
// sees the button react even when the response is instant. Shared by prompt-ui's iconTile
// and the bid pipeline's post-repaint re-flash (the pipeline's instant repaint rebuilds the
// clicked tile, so it re-applies the flash to the new node). Affects: prompt press feel.
export const PROMPT_PRESS_FLASH_MS = 180;

// play-test: how fast the bid prompt fades out after a pass click — long enough to read as
// "clicked, then gone" (the X's press feedback stays visible while it fades), short enough
// to feel immediate. Affects: pass dismissal feel.
export const PROMPT_PASS_DISMISS_MS = 150;

/** Card play animation timings (milliseconds). */
export const ANIMATION = {
  HAND_CLOSE_GAP: 300,
  AI_CARD_DELAY: 150,
  AI_CARD_FLY: 300,
  CROSSFADE_IN: 150,
  DECLARATION_REVEAL: 800,
  TRUMP_REVEAL: 600, // play-test: suit-to-purple transition; revealTrumpColor/revealTrumpGradient write it, the boundary mutation awaits it
  HINT_READING_PAUSE: 1200,
};

/** Snapshot geometry constants. */
export const SNAPSHOT = {
  CARD_WIDTH: 80,
  CARD_HEIGHT: 112,
  DEAL_SCALE: 0.5,
};

/** Skip FLIP animation when position delta is below this threshold.
 * why: sub-pixel rounding guard — browsers report fractional getBoundingClientRect
 * values, animating < 1px movement causes visible jitter without visible motion. */
export const FLIP_THRESHOLD_PX = 1;

/** Skip FLIP animation when rotation delta is below this threshold.
 * why: sub-degree rotation changes are invisible but cause a reflow + transition. */
export const FLIP_ROTATION_THRESHOLD_DEG = 0.5;

/*
 * Inline z-index values (set in JS, not via CSS classes) are pinned to the single
 * z-index scheme documented in the map comment at the top of skat.css — the four
 * zones (background / content / controls / transient). Each JS site carries its own
 * `// ... skat.css z-index map` comment; there is no separate JS-side ladder.
 */

/** Scale factor for hand decision / game type prompts vs normal prompt icons.
 * why: these prompts have fewer, more important choices than bid response —
 * larger icons match the decision weight. */
export const PROMPT_DECISION_SCALE = 1.4;

/** Bidding ceremony geometry. Timings live with the choreography
 *  (bidding/resolution-reveal.js RESOLUTION_DEFAULTS / DISSOLVE_DEFAULTS,
 *  bidding/banner-topple.js TOPPLE_DEFAULTS). */
export const CEREMONY = {
  /** Gatehouse icon scale during bidding ceremony (relative to role icon size).
   * why: the gatehouse is the prize — visually dominant during bidding, shrinks to 1× during flight. */
  GATEHOUSE_SCALE: 1.8,
  /** Mini banner scale on the claimed gatehouse icon.
   * why: measured from createGatehouseClaimed SVG — the banner slot is 43% of a standalone banner. */
  MINI_BANNER_SCALE: 0.43,
  /** Fraction of icon size for paired defender banner horizontal offset.
   * why: enough visual separation to read two banners without overlapping. */
  DEFENDER_LEAN: 0.22,
  /** Banner topple angle (degrees) on pass.
   * why: near-horizontal (90° = flat) but 5° lean hints at falling rather than resting. */
  TOPPLE_ANGLE: 85,
  /** The BIDDING opponent banner's golden-corridor rest (Run 22, Fix B,
   * `bidding-constellation.js#goldenCorridorRest`): the fraction of the free
   * corridor (between the gatehouse-side slot and the reach's outer stop) that
   * falls on the opponent-zone side of the rest point — 61.8% falls on the
   * gatehouse side.
   * why: 1/φ² ≈ 0.382 (golden by default, DESIGN §15) — the same split
   * `ARCH.CARD_OVERLAP` and `TRICK_SLOT_ICON_RATIO` already lean on. */
  OPPONENT_ZONE_SHARE: 0.382,
};

/** Winner highlight glow radius. */
export const WINNER_GLOW_RADIUS = 12;

/** Trick winner flash color — CTA amber, signals "this player goes next." */
export const WINNER_COLOR = "#D97706";

/** Trump color — unifies game type icons and trump card recoloring. */
export const TRUMP_COLOR = "#7E22CE";

/** The four-suit hex palette — the single home for a value that was five independent
 *  registrations (todo/suit-palette-single-home.md). Cycle-safe leaf: both
 *  `cards/cards.js#SUIT_COLORS` (which layers `grand: TRUMP_COLOR` on top) and
 *  `utils.js#suitColor` read from here directly, avoiding the cards.js→utils.js
 *  import cycle (card-template-cache.js→utils.js) a direct utils.js→cards.js
 *  import would create. */
export const SUIT_COLORS = {
  diamonds: "#D97706",
  hearts: "#BE123C",
  spades: "#046C4E",
  clubs: "#0369A1",
};

/** Opponent card back color by screen position — persistent per-opponent identity.
 *  Both opponents wear the same Artisan Mandala motif; color alone differentiates them
 *  and is reused on banner cloth so the same visual identity shows up across surfaces.
 *  See DESIGN.md § Player Identity. */
export const OPPONENT_BACK = {
  LEFT: "#334155", // Dark Slate
  RIGHT: "#475569", // Imperial Grey
};

/** Neutral/third card back — the deep-petrol surface used wherever no single player
 *  owns the cards: the human's own trick pile, the pooled defenders, and the skat
 *  pile while it is still contested (the human has no card-back identity). */
export const PETROL_BACK = "#082F49";

/** Per-screen-position identity color for banner cloth (bidding ceremony + trick slot
 *  watermarks). Human = pure white, matching the friend-aura light signal; opponents
 *  reuse their card back color for identity consistency across surfaces. */
export const IDENTITY_COLOR = {
  human: "#FFFFFF",
  left: OPPONENT_BACK.LEFT,
  right: OPPONENT_BACK.RIGHT,
};

/** Cloth color for banners that don't represent a specific player (leaderboard section
 *  markers, result-screen side watermarks). Vellum — the palette's canonical
 *  light surface, also the card face color. */
export const NEUTRAL_CLOTH = "#E2E8F0";

/**
 * The layout engine's phase vocabulary — the hand-bearing spatial-phase
 * screens (docs/dom/PHASES.md "LAYOUT spatial screen" lens). The solver takes
 * one of these as an input and reallocates board composition per screen from it
 * (the phase-adaptive reclaim — docs/layout/ENGINE.md § Spatial-phase input).
 * The human hand stays the fixed anchor across all of them
 * (initiatives/phase-adaptive-layout). The backend→screen map lives at the
 * layout boundary (board-layout.js `spatialPhaseOf`), keeping backend enum names out of
 * the engine.
 */
export const SPATIAL_PHASE = {
  BIDDING: "bidding",
  DECLARATION: "declaration", // Skat exchange & declaration
  TRICK: "trick",
};

/**
 * Screen-position angles in "our degrees" (0° = bottom, counter-clockwise).
 *
 * The +90° shift in toSvgRad() makes the mapping between degree values and
 * screen positions deeply non-obvious. Use these named constants instead of
 * raw numbers to prevent left/right swap bugs (see PR #104).
 *
 * Verify any new angle: x = cx + rx·cos(toSvgRad(deg)). If x < cx → LEFT.
 *
 *            TOP (180°)
 *           /          \
 *    UPPER_LEFT       UPPER_RIGHT
 *      (120°)           (240°)
 *         |               |
 *    LOWER_LEFT       LOWER_RIGHT
 *       (60°)           (300°)
 *           \          /
 *          BOTTOM (0°)
 */
export const SCREEN = {
  BOTTOM_DEG: 0,
  LOWER_LEFT_DEG: 60,
  UPPER_LEFT_DEG: 120,
  TOP_DEG: 180,
  UPPER_RIGHT_DEG: 240,
  LOWER_RIGHT_DEG: 300,
};

/**
 * Fan configuration per player type.
 * Angles reference SCREEN positions — see SCREEN constant above.
 */
export const FAN = {
  PLAYER_SPREAD_DEG: 70, // angular spread of player's hand on the oval
  OPP_SPREAD_DEG: 50, // angular spread of each opponent's hand
  PLAYER_CENTER_DEG: SCREEN.BOTTOM_DEG,
  OPP_LEFT_CENTER_DEG: SCREEN.UPPER_LEFT_DEG,
  OPP_RIGHT_CENTER_DEG: SCREEN.UPPER_RIGHT_DEG,
  PLAYER_RADIUS: 1.15, // player hand further from sun (pushes hand down)
  OPP_RADIUS: 0.85, // opponents closer to sun (pulls them in from edges)
  // TRICK_SCALE removed — now computed by layout-solver.js
};

/** Player-zone aura band padding = cardHeight × this ratio.
 * Adds breathing room around the fan in both radial thickness and angular sweep.
 * Consumed by `arc-aura.js#archBandMetrics`; see docs/layout/ZONES.md § Player zones. */
export const AURA_BAND_PADDING_RATIO = 0.15;

/**
 * Natural-arch geometry for the human hand (player-only). The hand radiates
 * from a focal point BELOW it — middle raised, outer cards splaying outward —
 * a deliberate break from the opponents' shared sun above. See
 * docs/layout/FAN.md § Natural-arch fans and the spec
 * specs/2026-06/2026-06-08-player-hand-natural-arch-design.md.
 *
 * Both values are play-test knobs centralized here. The parametric spread
 * passed to `fanCardAngles` is `2 × LEAN_DEG`.
 */
export const ARCH = {
  // play-test: outer-card lean in degrees (= 1/φ³ ≈ 23.6; golden by default,
  // legibility may override — affects how splayed the held hand reads). Capped
  // down by the depth-fit clamp when the arch would drop below the player band.
  LEAN_DEG: 23.6,
  // play-test: the flattened outer-card lean the fan settles to at a phone-portrait
  // aspect. A portrait fan is a pinched triangle radiating from far below — the card
  // tops splay to the edges while the bottoms converge inward, leaving large dead
  // space beside the bottoms (~34% of the width at the full lean, on both phone- and
  // tablet-portrait). Flattening the lean directly spreads those bottoms outward to
  // fill it (a fuller, more rectangular hand). 7° trades the held-arch curve for
  // utilitarian screen-fill — a deliberate UX-over-elegance call at the most extreme
  // portrait. why: docs/layout/FAN.md § Portrait-adaptive lean.
  PORTRAIT_LEAN_DEG: 7,
  // play-test: the aspect-ratio (width/height) band over which the lean ramps between
  // the flat portrait target (at/below FLAT — the most-portrait phones) and the full
  // arch (at/above FULL — square and landscape). Keyed on ASPECT, not width or height:
  // "how portrait is it" is exactly the pinched-triangle signal, and it scales the
  // flatten with portrait-ness — phone-portrait (~0.46) flattens fully, tablet-portrait
  // (~0.75) gets a gentler flatten, square/landscape keep the full arch. Landscape's
  // own flatten stays the depth-fit clamp's job (its aspect > 1 → full lean here, then
  // the clamp drops it). Continuous, so a window resizing toward portrait eases in.
  LEAN_FLAT_ASPECT: 0.5,
  LEAN_FULL_ASPECT: 1.0,
  // play-test: fraction of a card's width that overlaps its neighbour at a full
  // 10-card hand (≈ 1/φ² = 0.382; golden by default, legibility may override).
  // Drives the target half-width → R derivation (the one responsive-density
  // knob; analogous to maxSpreadForCardSpacing). Lower = wider, airier fan;
  // higher = tighter, more overlapped. Held here so the hand uses the ample
  // horizontal slack on landscape+desktop (it was cramming into ~40% of the
  // width while overlapping ~60%) — more of each card stays legible, which also
  // softens the follow-suit burying. Binds on those viewports; the oval-footprint
  // / screen-fit caps take over at tablet/phone-portrait, so it can't push cards
  // off-screen there. why: docs/layout/FAN.md § Natural-arch fans (width caps).
  CARD_OVERLAP: 0.382,
  // play-test: minimum exposed "tap strip" per card in CSS px — the visible
  // step between adjacent cards, which is what a thumb actually taps. The fan
  // spreads (airier than CARD_OVERLAP) until each card's strip reaches this,
  // bounded by the screen-fit / oval caps. Grounded in the ~44px platform touch
  // target; feel-tunable. Fires only where cards are small enough that the
  // golden-overlap strip falls short (phone-landscape); inert on desktop/tablet
  // (bigger cards already clear it) and phone-portrait (screen-clamped, so the
  // spread has nowhere to go). why: docs/layout/FAN.md § Demand-driven spread.
  TOUCH_STRIP: 64,
  // play-test: the airiest the demand-driven spread may get — cards always keep
  // AT LEAST this fraction overlapped, so the fan never spreads so wide the
  // adjacent tops separate and it reads as a disconnected row instead of a held
  // hand. Fences TOUCH_STRIP from above: on small cards (phone-landscape) the
  // touch target would otherwise push the cards to zero overlap, so this floor
  // reins the spread back to "as airy as it can be while staying connected."
  // Bigger cards reach the touch target before hitting this floor, so it's inert
  // there. why: docs/layout/FAN.md § Demand-driven spread.
  SPREAD_MIN_OVERLAP: 0.1,
  // play-test: indicator-pocket depth as a fraction of card height — how far the
  // band's inner edge is pushed toward the focal (below the cards) to open the
  // cup that holds the position icon (`:( → :(|`). Shallower than V1's 0.4 so the
  // inner edge rides higher and the cup's glyph stays clear of the screen's bottom
  // margin — a deeper cup pushed the edge, and the glyph with it, down into that
  // margin. Consumed by `arc-aura.js#archBandMetrics`.
  POCKET_DEPTH_RATIO: 0.3,
  // why: the pocket bottom is flattened to a floor line this many px above the
  // container's bottom edge so the cup never clips off-screen (`:(|`, the flat
  // base). An app-space gap, computed into `floorY` by `computePlayerSideGeometry`
  // and carried on the `playerArch` object into `computePlayerArchAura`.
  // play-test: lowered 16→10 so the human position glyph clears its readability
  // floor at phone-landscape. The cup is FLOOR-limited there (the bottom screen
  // edge, not the pocket depth), so this gap was the clamp pinning the human glyph
  // ~4px below the opponents' (21.7 vs 25.9 at phone-landscape bidding); trimming
  // it lifts the user's glyph to read largest (27.7), as its seat primacy asks
  // (PRIMACY § Seat is a primacy dimension). Inert at
  // desktop/tablet/portrait, where the cup is depth-limited and the floor never
  // binds. The human strip carries the glyph alone — no name hangs below it.
  POCKET_FLOOR_GAP: 10,
  // why: the SMALLEST screen-edge gap the human cup may sit to when it spends
  // the floor gap to keep its glyph at MIN_SIZE. Lowering the cup top to clear
  // the card envelope (POSITION_INDICATOR.CUP_CLEAR_*) would otherwise shrink the
  // glyph below MIN_SIZE at phone-landscape; instead the cup bottom extends past
  // the nominal POCKET_FLOOR_GAP toward the edge, bounded here. Only the cup's own
  // floor cap uses this — the aura band's `floorY` still reads POCKET_FLOOR_GAP
  // (and is null at phone-landscape anyway, the wings riding the bottom clamp).
  // Inert on roomy viewports, where the cup is depth-limited and never floor-bound.
  // Sized so the glyph holds MIN_SIZE at phone-landscape (the box bottom sits this
  // close to the edge; the visible ear/bid ink, centred in the box, stays well off).
  POCKET_HARD_FLOOR_GAP: 2,
  // play-test: the smallest RENDERED adjacent-card step (px) the touch-step
  // floor (layout-engine.js#applyTouchStepFloor, auto-tune two-regime
  // follow-through) will accept before capping hand growth back down — the
  // actual on-screen tap strip a thumb hits, not TOUCH_STRIP's "happy" design
  // target above. The two differ because the screen-fit cap in
  // computePlayerArch can override the happy target on a narrow viewport: a
  // BIGGER card's wider corner-extent eats MORE of the lateral room, so
  // growing the card can paradoxically SHRINK the rendered step (measured:
  // phone-portrait, a 146px card rendering a 24.0px step vs a 120px card's
  // ~26px — Run 13's comfort-ceiling growth ate the fan's own touch target).
  // Calibrated to this branch's OWN geometry, not literally main's number: at
  // phone-portrait the step holds a flat plateau of 25.9866px from the
  // absolute card-height floor up to ~182px, then declines — main's cited
  // "26.0" is the same plateau on a slightly different (pre-Run-13) oval, so
  // 26 exactly is unreachable here by a hair. 25.9 sits just inside the
  // achievable plateau (no boundary-exact float-noise risk — see
  // trick-slot-fill.js's own epsilon lesson) while landing the cap at the
  // plateau's real edge (~182-184px), inside the ~180-190 the design brief
  // asks for. Reuses the existing demandDrivenStep/fitLeanToBand algebra
  // (player-side-geometry.js) rather than re-deriving the geometry.
  RENDERED_STEP_FLOOR: 25.9,
};

/**
 * Flipped opponent-arch geometry — the corner analogue of the human's `ARCH`.
 * Each opponent arches IN toward the table centre from a focal point beyond the
 * fan toward its screen corner; the zone deepens into that corner (`:( → :(>`).
 * Same one-family construction as the human (shared `projectArchFan` /
 * `buildArchAura`); these are the opponent instance's knobs. Settled in
 * `/geometry-lab`. See docs/layout/ZONES.md § Indicator pockets and the spec
 * specs/2026-06/2026-06-14-opponent-flipped-fans-corner-pockets-port-design.md.
 */
export const OPP_ARCH = {
  // play-test: focal distance as a fraction of the fan→corner distance. Near 0 =
  // focal just beyond the fan (today-ish spread); near 1 = focal out in the
  // literal corner (wider splay). Settled A/B at ≈0.7.
  FOCAL_FRAC: 0.7,
  // Same outer-card lean as the human hand — one family, one splay.
  LEAN_DEG: ARCH.LEAN_DEG,
  // why: inset (px) from BOTH screen edges the corner pocket traces, so the cup
  // reads as distinct/on-purpose (mirrors the human's gap above the bottom edge).
  // One gap for the top and side inset alike (`clampToCorner`'s topY + sideX).
  CORNER_GAP: 14,
  // play-test: rounded-corner radius (px) of the pocket's corner close — a soft
  // fillet nesting into the screen corner, not a spiky tip (`clampToCorner`'s rFillet).
  CORNER_FILLET: 56,
  // play-test: pocket depth as a fraction of opponent card height (the human uses
  // 0.3; opponent cards are smaller, so the cup wants a deeper drop to hold the
  // indicator). Consumed by `arc-aura.js#computeOppArchAura`.
  POCKET_DEPTH: 0.65,
  // play-test: indicator depth into the cup as a fraction of the pocket depth
  // below the cards. Pushed deeper (0.55 → 0.78) so the strip — now taller with
  // the name row on top — clears the fan instead of clipping into the cards.
  // Consumed by `indicator-geometry.js#oppIndicatorSpot`.
  INDICATOR_FRAC: 0.78,
  // why: containment margin (px) shared by the width-fit cap and the zone close.
  // The cap (`opponent-side-geometry.js#fitOppArchToScreen`) keeps every card corner at
  // least this far inside each screen edge; the bounded corner clamp
  // (`arc-aura.js#boundedCornerClamp`) leaves cards this far inside the band edge.
  // Same number so the yielded zone always encloses the capped cards.
  CONTAIN_MARGIN: 2,
};

/**
 * Position-indicator icon (Geben/Hören/Sagen) shown in-play below the human
 * hand and beyond each opponent fan. Sized by a clamp (not a ray component) and
 * placed absolutely — see `player-side-geometry.js` (human) and
 * `indicator-geometry.js#oppIndicatorSpot` (opponents). All play-test knobs.
 */
export const POSITION_INDICATOR = {
  // play-test: icon size as a fraction of card height. Downsized from 0.44 to
  // leave room for the declarer-state cells (bid + Hand + Ouvert) that now ride
  // the strip beside it, and to read as a quiet board annotation rather than a
  // headline. Clamped down against the pocket's vertical room so it can't
  // outgrow a short viewport's cup.
  ICON_RATIO: 0.22,
  // play-test: the position glyph's readability floor — the smallest --indicator-
  // size at which the Geben/Hören/Sagen glyph (and the strip text that scales off
  // the same var — the declarer's bid cells, the opponents' etched name row) still
  // read on a phone. Below it the glyph is a
  // "comically small" annotation (Andre). The size is decoupled per seat: the
  // human grows to this floor against its roomy central cup, while the opponents
  // stay capped by their cramped corners (PRIMACY § Seat is a primacy dimension —
  // the user's glyph outranks theirs). So the floor lifts the human glyph above
  // the bare ICON_RATIO target at phone-landscape (≈22→28 in bidding; the cup caps
  // it short of the full 32) while the corner caps hold the opponents near 26.
  // Inert at desktop/tablet, where the ICON_RATIO target already clears it.
  MIN_SIZE: 32,
  // why: the human strip's flat top must clear the REAL card-bottom envelope
  // beneath it, not the idealized apex line (`anchorY + cardHeight/2`) the cup
  // once used — even card counts seat no card at the apex, and the arch curves
  // down away from it, so any card the strip spans dips below that line
  // (indicator-geometry.js#computeHumanIndicator). This is the half-width of
  // the central band the cup top clears, in px. 50 is a glyph-preserving band: the
  // phone-landscape glyph holds MIN_SIZE up to half ≈ 53 across hand counts, and
  // beyond that a wider band measures the arch's lower shoulders, pushing the cup
  // top down until the glyph starves below MIN_SIZE. 50 covers the COMMON declarer
  // strip (Grand/suit Hand, 2-digit bid ≈ 3.1×MIN_SIZE ≈ 100px → half ~50, measured
  // in-browser, clears +4px), NOT the widest variant (3-digit bid + Hand + Ouvert ≈
  // 4.58×MIN_SIZE ≈ 147px → half ~73): clearing that would starve the glyph. The
  // widest variant's ≈1px overhang is an accepted box-vs-ink residual — the strip
  // container is a transparent flex box (skat.css .position-strip-main paints no
  // background/border), so the overlap is box-only and the glyph ink stays centred
  // off the card. Closed won't-do 2026-07-11 (the drift the alternative fix would
  // have carried is sub-perceptual on roomy viewports and earned on phone).
  CUP_CLEAR_HALFSPAN: 50,
  // play-test: px of clearance the cup top leaves below that card envelope — a
  // small breathing gap so the strip box isn't pixel-flush against the card
  // corners. Affects how snug the strip sits under the hand at phone-landscape.
  CUP_CLEAR_MARGIN: 2,
  // why: the OPPONENT declarer's main row at its widest, as a multiple of
  // --indicator-size — icon (1.0) + gap (0.22) + bid (≤3 digits at 0.62 font ≈
  // 1.1) + gap (0.22) + Hand (0.82). The human's own strip is wider (it keeps the
  // Ouvert cell), but only the opponent corners bind the clamp — the human cup is
  // centred with the whole board width as room — so the opponent worst case, not
  // the absolute widest, is the right bound here.
  // Drives the screen-fit clamp (`indicator-geometry.js#assembleIndicator`), whose
  // on-screen contract layout-engine.indicator.test.js pins; the unit value was
  // set from the rendered strip's phone-viewport measurement (no overflow at
  // 393×851 / 800×1280 — pre-re-anchor measurement, taken against the old synthetic
  // phone-portrait cell; the reference phone-portrait cell has since moved to the
  // real Pixel 5's 352×638, not re-verified here — see
  // todo/reference-viewports-real-devices.md).
  STRIP_W_UNITS: 3.4,
  // why: the declarer corner's third-pass footprint (auto-tune Slice B, item B) —
  // STRIP_W_UNITS's own breakdown (icon 1.0 + gap 0.22 + bid ≈1.1 + gap 0.22 +
  // Hand 0.82) minus the icon cell AND its adjoining gap (1.0 + 0.22 = 1.22),
  // the two terms cornerIndicator's third pass drops from the main row once even
  // the bare glyph can't clear CORNER_GLYPH_MIN_SIZE. A narrower target footprint
  // lets the strip-fit/fan-pocket caps clear a LARGER size than the full main row
  // did — the glyph's shed is what buys the bid+underline (primary, never hidden)
  // more room, not merely a smaller thing to draw.
  NO_GLYPH_STRIP_W_UNITS: 2.18,
  // why: the strip's height as a multiple of --indicator-size — the opponents'
  // name row (the persona bust, `.position-strip-persona`'s 1.2×size square,
  // OUTSIZES the 0.5×size etched-name svg beside it and so dominates the row's
  // height — skat.css § Position Indicators) + the container's own row gap
  // (0.18) + the main row (1.0, `.position-strip-main`'s fixed height). Both
  // extents matter because the strip is rotated to the fan lean (the
  // screen-space footprint is the rotated bounding box, not the unrotated
  // width). Corrected from 1.6 (a stale name-row-only estimate that predated
  // the persona bust — auto-tune Run 13 follow-through, Bound 1): the old
  // value under-modeled the real strip by ~50%, invisible everywhere the
  // screen-edge cap this feeds had slack, until Run 13's grown `desired`
  // exposed it against the fan-pocket bound (indicator-geometry.js#fanPocketCapAtSpot).
  STRIP_H_UNITS: 2.38,
  // why: the opponent NAME row's rendered width, as a multiple of --indicator-
  // size — persona bust (1.2, `.position-strip-persona` — the STRIP_H_UNITS
  // comment's own figure) + the row's own gap (0.14, `.position-strip-name-row`)
  // + the etched name (`.position-strip-name`, height-sized at 0.5×size, width
  // following the wordmark's own SVG viewBox aspect via `aspectRatio` — CSS
  // computes it, not this constant). Basis: measured against the REAL rendered
  // DOM (a throwaway harness reusing position-indicator.js#buildSeatNameRow,
  // headless Chromium, --indicator-size 200px) rather than derived purely from
  // the viewBox numbers, so any box-model rounding the pure math would miss is
  // caught — none found (both wordmarks landed within 0.001 units of the pure
  // calc). "Lothar" (viewBox 3284×1250, aspect ≈2.627) is the WIDER of the fixed
  // AI pair — "Stef" (2028×1250, aspect ≈1.622) — so it's the one this constant
  // models: 1.2 + 0.14 + 0.5×2.627 ≈ 2.65, rounded up a hair to 2.66 for a small
  // safety margin. Feeds `indicator-geometry.js#cornerIndicator`'s pass-1
  // (name-row-assumed) footprint width, declarer-aware-opponent-corner-strip-
  // footprint: below STRIP_W_UNITS (3.4), as the design expected — a corner
  // whose name row is the binding footprint is narrower than one carrying the
  // declarer strip, which is the whole point of the per-corner solve.
  NAME_ROW_W_UNITS: 2.66,
  // why: px the rotated strip's bounding box keeps inside each screen edge — the
  // indicator analogue of OPP_ARCH.CONTAIN_MARGIN. The vertical cup clamp
  // (computeHumanIndicator) handles SHORT viewports; this handles NARROW ones
  // (portrait — a tall cup but a tight corner), where the wider strip would
  // otherwise run off-screen.
  SCREEN_FIT_MARGIN: 6,
  // why: the relaxed screen-edge margin `cornerIndicator` spends ONLY once a
  // corner has already proven starved (its pass-2 solve at the nominal
  // SCREEN_FIT_MARGIN falls below CORNER_GLYPH_MIN_SIZE) — the opponent-corner
  // analogue of the human pocket's own POCKET_FLOOR_GAP → POCKET_HARD_FLOOR_GAP
  // relaxation (indicator-geometry.js#computeHumanIndicator), and the same
  // value as both sibling gaps (that one and FAN_POCKET_MARGIN). Deliberately
  // NOT a global SCREEN_FIT_MARGIN retune: spending the breathing gap
  // everywhere to fix two starved cells would also move every roomy viewport
  // for no reason (e.g. tablet-portrait 800×1280's corner glyph, currently
  // unaffected by the starve, would shift with it). Safe to spend at the
  // screen edge specifically because the strip container paints no
  // background/border (skat.css .position-strip-main) — a box sitting this
  // close to the edge still keeps its glyph ink inset, the same box-vs-ink
  // argument CUP_CLEAR_HALFSPAN's comment makes.
  HARD_SCREEN_FIT_MARGIN: 2,
  // play-test: the smallest --indicator-size (px) at which the opponents' etched
  // name row still reads. Below it the name shrinks past legibility (the row is
  // 0.5×size tall, so this is a ~12px name floor) AND its rotated box starts to
  // clip the fan, so the name is dropped and the seat keeps the glyph alone. The
  // engine's per-corner cap shrinks each opponent's own size into this floor at the
  // tight phone corners, where a sub-legible name would be clutter, not identity.
  NAME_MIN_INDICATOR_SIZE: 24,
  // play-test: the smallest --indicator-size (px) at which the position GLYPH
  // itself (Geben/Hören/Sagen, the must-read secondary — PRIMACY § tier 5) still
  // reads at an opponent corner. Auto-tune Slice B: the aspect-aware sweet-spot
  // (item D) tightens the corner pockets further on portrait, so the two-regime
  // solve above (name-row-assumed, then bare-main-row) can still land on a
  // sub-legible glyph with nothing left to shed — a 5–14px icon reads as a smudge,
  // not iconography. Below this floor `cornerIndicator` doesn't shed immediately:
  // a corner that falls short here has PROVEN itself starved, so it earns one
  // more rung first — the same pass-2 footprint re-solved against the relaxed
  // HARD_SCREEN_FIT_MARGIN (the two 352-wide phone-portrait cells recover a
  // glyph exactly there, opponent-identity-hidden-narrow-portrait-cells). Only
  // once THAT also misses the floor does the shed fire (PRIMACY: a secondary at
  // its floor disappears): a non-declarer corner has nothing else to show, so
  // the whole indicator hides (the existing `spot.size > 0` build/reflow
  // convention); a declarer corner keeps its primary bid/Hand/Ouvert cells
  // (never hidden) and re-solves the strip's WIDTH without the icon cell
  // (NO_GLYPH_STRIP_W_UNITS below, also at the relaxed margin once the corner
  // has entered the starved branch), which is narrower and so can clear MORE
  // size, not less — shedding the glyph earns the strip room rather than just
  // hiding a symptom. "Order of 16–18" per Andre's brief; provisional pending
  // his feel pass on real glass (TUNING-JOURNAL Run 19's constellation floor is
  // the precedent for how this gets confirmed).
  CORNER_GLYPH_MIN_SIZE: 17,
  // why: px clearance the corner strip keeps from its OWN fan's wing cards
  // (indicator-geometry.js#fanPocketCapAtSpot) — the fan-pocket sibling of
  // SCREEN_FIT_MARGIN (the screen-edge clearance). Same scale as OPP_ARCH.CONTAIN_MARGIN,
  // the fan's own screen-edge tolerance, since both are "close but not touching" gaps
  // against a card corner.
  FAN_POCKET_MARGIN: 2,
};

/**
 * Reference sizes at the 2560×1440 baseline.
 * Each component has its own independent size — nothing derives from anything else.
 * These are starting points; tune during play-testing.
 */
export const REFERENCE = {
  SCREEN_WIDTH: 2560,
  SCREEN_HEIGHT: 1440,
  PLAYER_CARD_HEIGHT: 288, // px at reference — ~1.25× trick slots (auto-tune Run 7: desktop bold-up, was 260)
  OPP_CARD_HEIGHT: 165, // px — smaller than player (atmosphere, not interaction). Run 7: was 140. At 170+ the wider fans start pressing the bidding skat deal-home into its yield rung at tablet — once avoided as a cross-phase "divergence", but that "byte-identical" framing was retired 2026-07-03. Now a free feel value; a future tuning pass may raise it (the skat yielding in bidding is fine).
  TRICK_SLOT_HEIGHT: 230, // px — near card-sized for readable watermarks (Run 7: was 210)
  PROMPT_ICON_HEIGHT: 52, // px — icon tile content (prompt icons scale from this; Run 7: was 48)
  ROLE_ICON_SIZE: 110, // px — gatehouse / battle standards (Run 7: was 100; corridor clamp caps at ~111 at ref — see player-side-geometry computeRoleIconSize)
  PILE_CARD_HEIGHT: 150, // play-test: face-down card-back height in a trick pile
  WATERMARK_SIZE: 154, // px — game type icon in triangle center (110 × 1.4)
  BID_NUMBER_SIZE: 56, // px — 3.5rem × 16px base at reference screen
  GAP: 12, // px — tight; surplus fills via weight 4, no dead space
};

/**
 * Sizing groups — determines how eagerly each component absorbs size changes.
 * Higher weight = more elastic (absorbs more surplus/deficit).
 * Values are starting points; tune during play-testing.
 */
export const SIZING_GROUPS = {
  UX: { weight: 1 }, // player cards, trick slots, buttons — resist change
  VISUAL: { weight: 2 }, // opponent cards, role icons, trick piles — moderate
  FILLER: { weight: 4 }, // gaps — most elastic
};

/**
 * Minimum sizes — hard floor, never go below these.
 */
export const MIN_SIZES = {
  PLAYER_CARD_HEIGHT: 44, // touch target minimum
  OPP_CARD_HEIGHT: 30, // must remain recognizable
  TRICK_SLOT_HEIGHT: 44, // touch target minimum
  PROMPT_ICON_HEIGHT: 32, // icon tiles still recognizable at this size
  ROLE_ICON_SIZE: 24, // still recognizable
  PILE_CARD_HEIGHT: 40, // legibility floor — below this the trick piles hide entirely
  WATERMARK_SIZE: 44, // play-test: "visible implies legible" (auto-tune Run 15) — 30 rendered comically tiny even where genuinely visible. The FINISHED-only watermark's own "still visible" floor (applyWatermarkSizing, trick-slot-fill.js); the trick-play gate chain this once also fed retired with game-type-icon-on-skat-pile (PR-B).
  BID_NUMBER_SIZE: 20, // still readable
  GAP: 4, // minimal separation
};

/**
 * Hide thresholds for the off-ray secondary band (trick piles, prev-trick mini).
 * A hide threshold is a CLEARANCE GATE at useful size, NOT a resting floor: above
 * it the component renders at its natural scaled size; at or below it the component
 * hides entirely (it never rests AT the threshold). This is distinct from
 * `MIN_SIZES.PILE_CARD_HEIGHT`, which is the constructive resting clamp the pile
 * card is sized to — see layout/CLAUDE.md § MIN/MAX constants (defensive vs
 * constructive). All three are keyed off the solved pile-card height; their relative
 * magnitudes encode the PRIMACY sacrifice order (docs/layout/ENGINE.md
 * § Secondary-band hide thresholds — piles, then mini, then the floor-kept skat).
 *
 * Lowered in auto-tune Slice B (item A, 2026-07-29): Andre's settled call is
 * presence over vanishing — the count piles and mini are presence-floor
 * secondaries (TUNING-JOURNAL § Alignment item 2) that may drop BELOW the old
 * 65/50 values, so a 46–53px face-down stack still reads at the phone matrix
 * instead of hiding outright. Measured pile-card height at the four real
 * mobile cells (constant across phases — piles ride `scaleFactor`, not the
 * ray): 719×243 → 46.0, 719×352 → 53.1, 352×638 → 50.7, 352×719 → 53.1. 48/47
 * clear all three ≥352-height cells (min 50.7) with ≥1.7px of margin and keep
 * both hidden at 719×243 (46.0, margin ≥1) — a deliberate choice, not a side
 * effect: that cell carries its own tracked reds
 * (todo/phone-landscape-tuning-real-cell) this pass doesn't touch, and its
 * broader composition wasn't judged fit to add two more visible secondaries
 * to without a dedicated look. (Review round: the first-shipped MINI_CARD_HEIGHT
 * of 45 was BELOW the 46.0 measured floor, so `pileCardHeight <= miniThreshold`
 * never actually fired at 719×243 — the mini rendered there despite this
 * comment's own stated intent. 47 sits strictly between the 719×243 floor and
 * the ≥352-cells' minimum (50.7), so it hides at exactly the cell described
 * above and nowhere else — still `pile(48) > mini(47) > skat(40)`, the
 * monotone order ENGINE.md documents.)
 *
 * Final values — the geometry-lab cross-viewport tuning pass closed 2026-08-10
 * without revisiting these (docs/layout/TUNING-JOURNAL.md § Close-out — 2026-08-10
 * feel-pass sign-off, verdict 4). Exposed to geometry-lab as flat override keys
 * (`pileHideThreshold`, `miniHideThreshold`).
 */
export const HIDE_THRESHOLDS = {
  PILE_CARD_HEIGHT: 48, // play-test: trick-count piles hide below this useful size — clutter beside the hand
  MINI_CARD_HEIGHT: 47, // play-test: prev-trick mini hides below this — lower than the piles, so it outlives them
};

/**
 * Comfort ceilings — the demand signal for the phase-adaptive reclaim: each of the
 * three hand-relief gates fires only while the solved hand sits BELOW its ceiling
 * (a functional max-comfortable-size cap, NOT a desktop guard), so a viewport whose
 * hand already reads well keeps the band slack. That the phases then match on a roomy
 * viewport is a consequence of the ceiling, not an invariant — desktop is free to
 * differ across phases (the "byte-identical" framing was retired 2026-07-03). The
 * gates that share this demand signal live in docs/layout/ENGINE.md § Spatial-phase
 * input; this is the value's home.
 *
 * The card ceiling is PER-PHASE, the slot ceiling a single value (the slot is a
 * beneficiary only in trick — bidding/declaration drop it). Per-phase is not just
 * flexibility for its own sake: it's the data line that makes two of the
 * initiative's future layers tunable without code — a per-phase phone cap (keep
 * one phase's hand smaller, e.g. to clear a banner) and offensive-on-desktop for
 * one phase (raise a phase's ceiling above the desktop card). A shared ceiling
 * would silently assert the hand's comfortable size is identical every phase,
 * which this initiative does not hold (the hand sizes per phase).
 *
 * TWO REGIMES, ONE RULE (the desktop primacy rebalance, auto-tune Run 13): the
 * effective card ceiling is max(PLAYER_CARD_HEIGHT[phase], HEIGHT_FRACTION × viewport
 * height), resolved once at the computeLayout boundary (layout-solver.js
 * withComfortCeilings). The per-phase absolutes are the PRESSURE-regime caps — each
 * encodes a phone-landscape no-clip limit the e2e fences found (116/100/108), and they
 * keep governing wherever the proportional term is smaller. The shared HEIGHT_FRACTION
 * is the ROOMY-regime comfort line: on viewports with slack it lifts the demand gates
 * so the hand claims the dead space the absolutes (tuned as phone caps) silently left
 * banked — the "offensive on desktop" layer, now a value instead of a future. One max(),
 * no viewport special-casing; the fences stay the real growth arbiter everywhere.
 *
 * Final values — the geometry-lab cross-viewport tuning pass closed 2026-08-10
 * without revisiting these (docs/layout/TUNING-JOURNAL.md § Close-out — 2026-08-10
 * feel-pass sign-off). Exposed to geometry-lab as flat override keys
 * (`cardComfortCeiling`, `slotComfortCeiling`), which force the final
 * value and bypass the max(). The slot ceiling tracks the card's at the REFERENCE
 * card:slot ratio (~1.25).
 */
export const COMFORT_CEILINGS = {
  // play-test: the roomy-regime comfort line — the hand's comfortable height as a
  // fraction of viewport height, shared across phases (cross-phase alignment at
  // desktop is the point — Andre, Run 13: bidding/declaration/trick card sizes
  // "somewhat aligned"). 0.24 lands the desktop/tablet/portrait hand ~+20% over the
  // Run-7 rest values while staying under the geometric max (~0.28 at desktop-ref),
  // so the opponent-yield gate stays naturally false on roomy viewports.
  HEIGHT_FRACTION: 0.24,
  // Per-phase: the hand's comfortable size, by spatial phase. The per-phase split is
  // load-bearing here — bidding and declaration DIVERGE (see below); a tuner can move
  // any phase without touching code.
  PLAYER_CARD_HEIGHT: {
    // why: BIDDING rises to the SHRINK-TIER no-clip ceiling — higher than #1088's 100
    // (full-opponent) cap because the opponent fans now YIELD the top edge (OPP_YIELD
    // below), shrinking toward MIN so the oval rides further before an opponent card
    // escapes its aura-clip zone. This is the demand the engine's yield gate
    // (layout-engine.js solveWithOpponentYield) reads: at phone-landscape the
    // full-opponent hand (~111) sits below it, so the gate fires and the yielded solve
    // grows the hand to here; tablet+ is already ≥ this, so the gate is false and they
    // stay put — at current values that reads the same across phases (a consequence of
    // the ceiling, not a guard; desktop may be tuned to differ). e2e-validated at the shrunk opponent size
    // across the full matrix (tests/e2e/bidding-granular — assertOpponentCardsStayInZones).
    // Final per viewport — the geometry-lab cross-viewport tuning pass closed
    // 2026-08-10 without revisiting this value (TUNING-JOURNAL.md § Close-out).
    // The HIDE tier still raises it further once the opponents leave (the filed
    // follow-up).
    [SPATIAL_PHASE.BIDDING]: 116,
    // why: DECLARATION stays at #1088's 100 and does NOT yield (OPP_YIELD null below):
    // its tightest sub-state is the 12-card discard hand, whose wider/taller fan rides
    // the yield's oval lift-up into the discard prompt (tests/e2e/discard-granular —
    // the all-pairs no-clip net, assertNoBoardComponentsClip). The overlap is the oval ride-up itself,
    // not the hand height, so no yielding ceiling clears it — declaration's yield is
    // the off-switch the spec anticipated (the mechanism stays wired; bidding is the
    // demonstrator). The hide follow-up can revisit declaration once opponents leave.
    [SPATIAL_PHASE.DECLARATION]: 100,
    // why: the trick hand's no-clip ceiling. The move-rung rise (the trick-slot fill,
    // Run 6) grows the hand into the vacated focal — with no trick opponent-yield lever
    // (OPP_YIELD[TRICK] null), the hand rides the oval up until the arched opponent cards
    // hit their aura-clip zone (the e2e fence `assertOpponentCardsStayInZones`, which the
    // analytical sweet-spot under-reads by ~1px on the rotated corner). 120 (the old
    // slice-1 placeholder, set when the trick card never reached it) clips by ~1px at the
    // demonstrator; 108 holds the hand at Run 5's measured, fence-passing landing (the
    // slice grows the slots, not the hand). Final per-viewport value against the real
    // fence — the geometry-lab cross-viewport tuning pass closed 2026-08-10 without
    // revisiting it (TUNING-JOURNAL.md § Close-out); trick can rise further only once
    // it gains a yield lever.
    [SPATIAL_PHASE.TRICK]: 108,
  },
  // play-test: the pressure-regime slot cap ≈ trick card ceiling ÷ 1.25 (the REFERENCE
  // card:slot ratio). Like the card absolutes above, lifted proportionally on roomy
  // viewports: the effective ceiling tracks the RESOLVED card ceiling at that same
  // ratio (withComfortCeilings), so the reclaim's slot cap scales with the regime.
  TRICK_SLOT_HEIGHT: 100,
};

/**
 * The prompt icon's ray-GROWTH aspiration ceiling — the inverse of the comfort
 * ceilings above (those grow the hand TOWARD a target; this caps how far
 * `reallocateAbsentBand` (layout-solver.js) may grow the prompt icon past
 * REFERENCE.PROMPT_ICON_HEIGHT once BIDDING/DECLARATION drop the unplayed trick
 * slot and its freed ray band flows to the survivors). Without a ceiling the
 * icon rides that band all the way to Run 13's 77px at desktop-ref — bigger
 * than the prompt ever needs (Andre, Run 14: "i dont need the prompts to be as
 * big"; TUNING-JOURNAL Run 14). Only a beneficiary of the dropped band can
 * reach this — TRICK never sees it (there the prompt IS the dropped
 * component, carried through at its phase-stable full-chain value instead).
 *
 * play-test: dial directly against the measured matrix
 * (`node scripts/dev/measure-layout.js`) — auto-tune prompt-icon-lift-first-
 * then-shrink, targeting +20–40% over the pre-Run-14 shrink-pinned desktop
 * values (~32) at desktop-ref/desktop-1080/tablet-landscape.
 */
export const PROMPT_ICON_ASPIRATION_CEILING = 46;

/**
 * How far the opponent fans may yield the top edge per phase, to free the hand
 * card's top clamp (the first OFF-ray lever — docs/layout/ENGINE.md § Phase-relaxed
 * top clamp). A numeric value is a RENDERED-px shrink floor the opponents clamp
 * toward (kept ≥ MIN_SIZES.OPP_CARD_HEIGHT, which still floors the present opponent);
 * `null` = off. Applied post-solve by `applyOppYield` (layout-solver.js), gated
 * demand-driven by the engine so a viewport whose hand already clears its ceiling
 * never yields (desktop at current values); nothing forces the phases to match there.
 *
 * This phase ships the shrink tier (a numeric floor) + one demonstrator
 * (phone-landscape bidding); it only needs phone-landscape to yield while tablet+
 * stays put. The geometry-lab cross-viewport tuning pass closed 2026-08-10
 * (docs/layout/TUNING-JOURNAL.md § Close-out — 2026-08-10 feel-pass sign-off)
 * without retuning this value or deciding the PRIMACY hierarchy question below —
 * the demonstrator still yields both opponents symmetrically (they are equal at
 * phone). A future asymmetric-yield pass needs its own todo, not this closed one.
 */
export const OPP_YIELD = {
  [SPATIAL_PHASE.BIDDING]: 30, // demonstrator: shrink to the recognizability floor (MIN_SIZES.OPP_CARD_HEIGHT)
  // null = off (the spec's declaration off-switch): the yield's oval ride-up pushes
  // the 12-card discard fan into the discard prompt (see COMFORT_CEILINGS above), and
  // no yielding ceiling clears it, so declaration keeps #1088's full-opponent solve.
  [SPATIAL_PHASE.DECLARATION]: null,
  [SPATIAL_PHASE.TRICK]: null, // no yield — opponents frame the played cards
};

/**
 * Trick-phase trick-slot fill (the move rung) — the values behind the grow/spread/rise/yield
 * levers. The mechanism is `solveTrickSlots` (layout-solver.js); the design ladder and the
 * `opponents > human` primacy order (a documented yield-ORDER with no per-slot size knob yet
 * — YAGNI, CODING § delete before abstracting) are docs/LAYOUT.md § Trick-slot adaptation.
 *
 * Final per-viewport values — the geometry-lab cross-viewport tuning pass closed
 * 2026-08-10 without revisiting these (docs/layout/TUNING-JOURNAL.md § Close-out).
 * Geometry-lab overrides the rise live via the flat `humanSlotRise` key (mirroring
 * `cardComfortCeiling`), whatever phase the lab is solving.
 */
export const TRICK_HUMAN_SLOT = {
  // The human-slot rise serves two ends bundled in one lift, and they want different
  // magnitudes — so trick-slot-fill.js#applyHumanSlotRise takes max(relief, aesthetic) of
  // the two fractions below. Splitting them is what lets the desktop aesthetic retune not
  // starve the one cramped cell (game-type-icon-on-skat-pile, 2026-07-13): before the split
  // a single fraction drove both, so lowering it for desktop shrank the phone-landscape
  // hand ~12%. Both capped < 1 so the rise never reaches the sun, where the slot's facing
  // rotation degenerates (atan2(0, 0)); the portrait opponent-collision cap is separate
  // (trick-zone-border.js#maxRiseForSlotClearance).
  //
  // play-test: the SLOT AESTHETIC lift — the compositional rise a roomy board takes purely
  // to move the played-card slot into the vacated focal, as a fraction of the slot's sun
  // offset. Retuned 0.8→0.56 (Andre 2026-07-13): once the watermark stopped reserving the
  // sun, 0.8 lifted the slot too high off the player zone on desktop/tablet-landscape, so
  // this lands it ~halfway. On roomy viewports the hand already sits at its comfort ceiling,
  // so this fraction sets the slot alone; it dominates the relief term everywhere but the
  // cramped cell.
  SLOT_RISE_FRACTION: 0.56,
  // why: the sun-safety ceiling on the HAND-RELIEF lift. Where the hand is too cramped to
  // reach comfort (phone-landscape today), the rise grows to feed it — up to comfort, but
  // never past this fraction of the sun offset. This is the pre-retune 0.8: the relief case
  // wants the full lift the desktop aesthetic no longer takes.
  MAX_RISE_FRACTION: 0.8,
  // play-test: the slots' readable size target as a fraction of the PRE-FILL hand-card
  // height — keeps the played and held cards (the same physical object) visually
  // related while riding the adaptive sizing. ≈ 1.07 lands the demonstrator's ~90 px
  // off its ~84.5 px pre-fill hand. The no-clip fence wins over this aspiration.
  SLOT_SIZE_RATIO: 1.07,
  // play-test: the trick triangle's spread floor (vs the ~0.40 compact rest value) —
  // fans the slots out toward their seats into the lateral dead space. Trick-only and
  // gated, so bidding/declaration (their own constellation, #1063) and slack viewports
  // keep the compact triangle. why: a wider triangle opens central room for the grown
  // slots without stealing the hand's vertical ray budget. 0.65→0.85 in the dead-space
  // wave (Run 12): the side↔side slot cap scales with this floor, so the raise is what
  // unlocks portrait slot growth (phone-portrait slot 81→106, tablet-portrait 165→216);
  // desktop (slot already at its size target) and phone-landscape (its own caps bind)
  // are measured-insensitive to it. 0.95 was measured too far — the side slots ride
  // under the opponent fans at tablet-portrait: the spread has no fan-wall border
  // (unlike bidding's constellation), so the safe ceiling was empirical until the
  // border-aware bound below shipped (auto-tune Run 13 follow-through). DEMOTED to a
  // safety floor (todo/trick-spread-border-aware-bound) in one sense only: it still
  // sets the NATURAL (pre-border) aspiration `computeTrickScale`'s own `max()` reaches
  // for wherever no zone risk exists (every measured desktop/landscape viewport — the
  // border is a no-op there). It is NOT a floor the zone ceiling itself respects:
  // `zoneAwareTrickScaleCeiling` (layout/trick-zone-border.js) is a hard final cap, and
  // at tablet-/phone-portrait — where measured clearance at 0.85 is already a
  // near-touching 0.7–3px — the border can and does pull the spread BELOW 0.85 to hold
  // real clearance; a floor that clawed the ceiling back up would defeat the border at
  // exactly the viewport it exists for.
  SPREAD_SCALE_MIN: 0.85,
  // why: px clearance the trick-slot fill's zone-aware border keeps between a slot
  // polygon and its neighbouring zone-aura polygon (trick-zone-border.js). Same small
  // scale as the fan-pocket's own FAN_POCKET_MARGIN (POSITION_INDICATOR) — both are
  // "close but not touching" tolerances against a card-adjacent zone boundary. 10, not
  // an even 8, absorbs the border's own PRE-fill approximation slop (it solves against
  // a snapshot oval/zone, not the exact post-fill render) — measured at 7.98px real
  // clearance against an 8px target at tablet-portrait, a hair under from that
  // approximation gap; the e2e assertTrickSlotsClearZones checks the same 8px, so this
  // small buffer is what keeps the two honestly consistent.
  ZONE_CLEARANCE_MARGIN_PX: 10,
};

// why: px clearance the prompt-icon aura-clearance bound (prompt-aura-clearance.js#
// applyPromptAuraClearance, auto-tune two-regime follow-through) keeps between the
// promptZone box and the human zone-aura polygon — the same "close but not touching"
// scale as TRICK_HUMAN_SLOT.ZONE_CLEARANCE_MARGIN_PX (the trick slots' own zone-aura
// border) and POSITION_INDICATOR.FAN_POCKET_MARGIN (the opponent corner's fan-pocket
// border): all three are the same class of box-vs-zone-aura-polygon tolerance.
export const PROMPT_AURA_CLEARANCE_MARGIN_PX = 10;

// why: px clearance the prompt container-width bound (prompt-container-bound.js#
// applyPromptContainerBound) keeps between the promptZone box and the container
// edge — same "close but not touching" scale and family as
// PROMPT_AURA_CLEARANCE_MARGIN_PX just above, a different border (the container
// edge, not the human zone-aura) on the same box. Split into its own constant
// rather than reused: the two bounds compose (declaration-prompt-overflows-
// narrow-portrait), and conflating their margins would tie two independently
// tunable tolerances to one knob.
export const PROMPT_CONTAINER_MARGIN_PX = 10;

// play-test: the in-slot role watermark (declarer gatehouse / defender banners / Ramsch
// standard — render-trick.js) as a fraction of its slot's height. The slot's ghost sizes
// off its OWN slot, not the slot↔hand corridor value (`roleIcon.size`): the ghost's
// clearance is the slot box it paints inside, and the trick fill's grown slots crush the
// corridor (desktop 107→25 across the dead-space wave) while the slot itself grows.
// 1/φ² ≈ 0.382 (golden by default, DESIGN §15); the pre-wave desktop ratio was ≈0.47,
// so the golden cut renders in the familiar band.
export const TRICK_SLOT_ICON_RATIO = 0.382;

/**
 * Minimum sizes at the phone-landscape endpoint (h ≤ 400 px).
 * Only ray components with disproportionate cost on short viewports appear
 * here — `MIN_SIZES` is the desktop floor; these are the phone floors.
 * Interpolated component-wise over the 400→800 band by `adaptiveLayoutParams`.
 * See spec 2026-04-18 § C2.
 */
export const PHONE_MIN_SIZES = {
  PROMPT_ICON_HEIGHT: 24, // Google 24 dp touch-target guidance; two-icon tile stays 48 wide
  GAP: 2, // still reads as "not touching"
};

/**
 * The absolute smallest prompt icon we will ever render — the floor for the
 * container-width bound (`prompt-container-bound.js`), which shrinks the prompt
 * row to keep it on-glass at a narrow viewport.
 *
 * why: deliberately NOT `PHONE_MIN_SIZES.PROMPT_ICON_HEIGHT`, despite sharing
 * its value today. That one is an *interpolation endpoint* tied to the 400→800
 * height band (see its docblock); this is an *absolute* floor answering a
 * width-driven question, and the two must be free to move independently — a
 * retune of the phone-landscape endpoint must not silently redefine how small a
 * narrow-portrait prompt may go. (layout/CLAUDE.md § MIN/MAX constants —
 * defensive vs constructive use: the same conflation once cost ~5px of opponent
 * cards.) The value is Google's 24 dp touch-target guidance: at a narrower
 * viewport the row cannot fit at any legible size, and buttons rendering
 * off-glass is strictly worse than buttons rendering small.
 */
export const PROMPT_ICON_CONTAINMENT_FLOOR = 24;

/** Text size = promptIcon.height × this ratio (14.4px at reference 48px icon). */
export const LAYOUT_TEXT_RATIO = 0.3;

/** Hard floor for layout text — solver's icon minimum (32px) yields 12.8px naturally. */
export const MIN_LAYOUT_TEXT_SIZE = 13;

/** Viewport height (CSS px) at which adaptive layout params are fully phone-optimized.
 * why: phone landscape CSS height — Andre's real Pixel 5 reports 243px bar-shown
 * (display-scale raises DPR to 3.0625; measured over CDP, not stock device emulation —
 * todo/reference-viewports-real-devices.md). The 400 endpoint still comfortably covers
 * it, so the value is unchanged by the re-anchor. */
export const ADAPTIVE_MIN_HEIGHT = 400;

/** Viewport height (CSS px) at which adaptive layout params match desktop defaults.
 * why: smallest desktop/tablet height where solver defaults work without adaptation. */
export const ADAPTIVE_MAX_HEIGHT = 800;

/** Aspect ratio (width/height) at/below which the coupled sweet-spot's ratio solve
 * (goldenRatio/ryRatio only — layout-engine.js#adaptiveLayoutParams) fully engages
 * regardless of raw height — the portrait counterpart of ADAPTIVE_MIN_HEIGHT. why:
 * the height-only gate above never fires for a TALL-but-narrow viewport (tablet-
 * portrait's 1148/1280 sit past ADAPTIVE_MAX_HEIGHT), so the both-ends-bind solve
 * that phone-landscape gets never reached portrait — auto-tune Slice B. Own
 * constants, not ARCH.LEAN_FLAT_ASPECT/LEAN_FULL_ASPECT: same "how portrait is it"
 * signal, unrelated mechanism (fan lean vs oval ratios) — layout/CLAUDE.md § MIN/MAX
 * constants warns against borrowing an interpolation endpoint across mechanisms.
 * play-test: 0.7 covers all FOUR real portrait cells at full saturation, incl. the
 * LEAST portrait one (tablet-portrait bar-shown, 800×1148, aspect 0.6968) — the
 * approved D2 direction wants the both-ends-bind composition at every real
 * portrait cell, not a partial pull at the tablet ones (0.5 left tablet-portrait
 * bar-shown/fullscreen at only 39%/75% pulled, well short of Andre's "fill the
 * glass top-to-bottom" screenshots). */
export const ADAPTIVE_ASPECT_LOW = 0.7;

/** Aspect ratio at/above which the portrait ratio-solve term is a no-op (1.0, i.e.
 * exactly 1 — landscape and desktop MUST stay byte-identical, so this has to clamp
 * to exactly 1, not merely close). Square counts as "not portrait" here. */
export const ADAPTIVE_ASPECT_HIGH = 1.0;

// === Prompt box ratios (rendered-box math, mirrors today's CSS) ===

/** Cards the declarer discards to the skat after picking it up — always two. */
export const DISCARD_COUNT = 2;

/** .icon-tile padding = iconH × this ratio (mirrors skat.css:709 `0.22`). */
export const ICON_TILE_PADDING_RATIO = 0.22;

/** .prompt-actions gap = iconH × this ratio (mirrors skat.css:635 `0.15`). */
export const PROMPT_GAP_RATIO = 0.15;

/** Play-test: visual breathing room between prompt bottom and aura envelope. */
export const PROMPT_BOTTOM_GAP = 4;

/** Play-test: visual breathing room between prompt top and trick-slot bottom. */
export const PROMPT_TOP_GAP = 4;

/** Defensive margin (px) on the bidding-ceremony vertical clearance clamps
 * inside `computeRoleIconSize`. Absorbs sub-pixel rounding and the same
 * FP-divergence concern that `computeWatermarkClamp` documents.
 * why: load-bearing for the gatehouse-vs-banner non-overlap and
 * gatehouse-vs-viewport-edge constraints; raising it shrinks role icons
 * further at phone-landscape. */
export const CEREMONY_VERTICAL_MARGIN = 4;

/** The role-icon constellation's MINIMUM size — the hide threshold, phase-general
 * (read by both the BIDDING orbit and the DECLARATION settled arms). Below this
 * the gatehouse/standards stop reading as heritage iconography, so a piece that
 * can't hold it hides rather than rendering as noise (the orbit ladder's rung 4;
 * spec § _The ladder_, § _What "full size" is_). It is the floor the ladder never
 * sinks below, NOT the target it reaches toward — that target is the per-piece
 * `freeSize` the producer exposes.
 * play-test: judged on the real Pixel 5 at the fullscreen 719×352 cell (2026-07-17,
 * Andre, on glass): the live emergent size there is 22.55 CSS px (69 device px at
 * DPR 3.0625) and it still reads as iconography — this floor sits just under it,
 * superseding the earlier "22px was too low" note, which was judged against the
 * synthetic 851×393 cell no device presents (TUNING-JOURNAL Runs 17–19). At the
 * bar-shown 719×243 cell the emergent size (~9) stays far below any plausible
 * floor — hidden there is correct, not a tuning question.
 * Raising this past ~23 re-enters a broken band: the bidding solve's prompt-zone
 * border is state-dependent (~22.6 while the human's bid prompt shows, more when
 * it doesn't), so a floor between the two flips the whole family's presence
 * across bidding sub-states at that cell (TUNING-JOURNAL Run 19). */
export const ROLE_ICON_CONSTELLATION_FLOOR = 22;

/** How far the BIDDING constellation reaches into the bidding void, as a multiple
 * of the trick-corridor `freeSize` the board affords at rest. 1× is the old
 * behaviour (the constellation floats at the trick-play role-icon size in a large
 * bidding void — dead space below the primacy ladder); above 1× the gatehouse and
 * banners grow to fill that slack, each still capped by its own ray border (the
 * human at the prompt, an opponent at its fan wall, the skat yielding above). The
 * container edge-fit is the hard upper bound regardless. why: the bidding void is
 * dead space and dead space is infinitely sacrificeable to a primary/near-primary
 * (docs/components/PRIMACY); the constellation claims it. play-test: start bold,
 * dial live against the matrix — todo/auto-tune, the bidding-constellation wave. */
export const BIDDING_REACH_SCALE = 2.5;

/** .icon-tile { border: 1px solid }; box-sizing border-box adds it to the
 * declared width on each side. why: matches skat.css:798 — keep in sync. */
export const ICON_TILE_BORDER_PX = 1;

/** Satoshi line-height "normal" (browser default, no explicit `line-height`
 * on .btn). why: measured 2026-04-26 against rendered DOM (Chromium) at the
 * then-current `.btn` narrow-viewport size, 13.6px → 18px content ≈ ×1.32.
 * `normal` is font-metric-derived, so proportional to font-size in principle —
 * but Chromium rounds ascent/descent to whole pixels per size, so the ratio is
 * only approximate, not a true constant: re-measured 2026-08-17 at the raised
 * 16px narrow size, actual content height is 22px (38px box − 16px padding),
 * a ×1.375 ratio, not ×1.32. Left unchanged — the model's resulting 0.88px
 * under-prediction (37.12 vs 38) still clears prompt-box-model-vs-rendered
 * .spec.js's ±5px tolerance, and a second data point isn't enough to fit a
 * better constant (its own note: an under-prediction is the unsafe direction,
 * so this is flagged, not ignored, if a third size ever tightens the margin). */
export const TEXT_LINE_HEIGHT_FACTOR = 1.32;

/** .prompt has `gap: var(--space-md)` between flex-column children
 * (skat.css:651–657). Matches design token --space-md = 12px. */
export const PROMPT_STACK_GAP_PX = 12;

/** Bid-prompt .bid-value font-size = --prompt-icon-size × this ratio.
 * why: mirrors skat.css:690 — `font-size: calc(var(--prompt-icon-size) * 0.8)`. */
export const BID_VALUE_FONT_SIZE_RATIO = 0.8;

/** Digit-width reservation for the bid prompt's .bid-value — the widest display a bidding
 * sequence shows (three digits plus a trailing "?"/"!", up through the highest legal bid), so
 * the pass/accept tiles either side never nudge as the value changes. `buildBidValue` applies
 * it as `min-width: {length}ch` via count-up-reserve.js; `bidPromptBox` mirrors that width.
 * The two must agree — a shorter template here silently under-reserves AND under-models. */
export const BID_VALUE_RESERVE_TEMPLATE = "888?";

/** The top-right Menu affordance — engine-sized and -placed (it has no bar; the
 *  info bar was retired once declarer-state moved to the position strips), on both
 *  the board and the start screen (layout/menu-affordance.js#applyMenuAffordanceVars,
 *  the single writer for both paths). Square button + icon and the corner inset
 *  scale with the smaller viewport dimension (the scarce axis on landscape phones),
 *  clamped at both ends. Doubled 2026-07-14 (menu-on-start-screen-and-sizing, Andre's
 *  thumb-friendly call): a real tap target, not a corner afterthought. The new curve
 *  is EXACTLY 2× the old at every viewport — the same curve `--result-affordance-size`
 *  used to derive by doubling `--menu-size` in JS (board-layout.js); that derived var
 *  and its ×2 are retired, skat.css `.result-affordance` now reads `--menu-size`
 *  itself. INSET_* and ICON_RATIO are unchanged — position and icon proportion are
 *  out of this change's scope (`.hint-button` shares `--menu-inset`). Consumed via
 *  `--menu-*`. */
export const MENU = {
  SIZE_RATIO: 0.1,
  MIN_SIZE: 60,
  MAX_SIZE: 88,
  ICON_RATIO: 0.6, // icon side as a fraction of the button side
  INSET_RATIO: 0.02,
  MIN_INSET: 8,
  MAX_INSET: 18,
};

// === .btn .btn-primary shape — captured 2026-04-26 against skat.css ===
// .btn rule (line 742): font-size var(--fs-md), padding var(--space-lg) var(--space-2xl)
// .btn-primary override (line 776): font-size 19px
// @media (max-width: 900px) `.btn` override: font-size var(--fs-sm), padding var(--space-sm) var(--space-lg)
// If skat.css .btn changes, update both regimes here. The Playwright sanity
// spec catches drift loudly.

/** Viewport-width threshold below which `.btn` uses the @media-narrow shape. */
export const BTN_NARROW_VIEWPORT_THRESHOLD_PX = 900;
/** `.btn` font-size at narrow viewport (var(--fs-sm) = 1rem = 16px). */
export const BTN_NARROW_FONT_SIZE_PX = 16;
/** `.btn` vertical padding at narrow viewport (var(--space-sm) = 8px). */
export const BTN_NARROW_PADDING_Y_PX = 8;
/** `.btn` horizontal padding at narrow viewport (var(--space-lg) = 16px). */
export const BTN_NARROW_PADDING_X_PX = 16;
/** `.btn-primary` font-size at wide viewport (skat.css `.btn-primary` rule, 19px). */
export const BTN_WIDE_FONT_SIZE_PX = 19;
/** `.btn` vertical padding at wide viewport (var(--space-lg) = 16px). */
export const BTN_WIDE_PADDING_Y_PX = 16;
/** `.btn` horizontal padding at wide viewport (var(--space-2xl) = 32px). */
export const BTN_WIDE_PADDING_X_PX = 32;

// === Hint box ratios (rendered-box math, mirrors today's CSS) ===

/** Maximum wrapped lines the engine models.
 * why: content discipline — hint messages must fit within this at
 * phone-landscape; shorten violators rather than widening the bound. */
export const HINT_N_MAX_LINES = 2;

/** Engine-owned cap: `.hint-reason` consumes this via `--hint-max-width`. */
export const HINT_MAX_WIDTH_RATIO = 0.7;

/** Caps the hint text-base size at this fraction of container height.
 * why: keeps hint text from dominating short viewports (phone-landscape).
 * Original 0.024 mirrors a pre-engine CSS rule (`min(..., 2.4vh)`); the
 * value stayed when sizing moved into the engine. */
export const HINT_CONTAINER_HEIGHT_CAP_RATIO = 0.024;

/** hint font-size = hint-text-base × this ratio (mirrors skat.css:2118 `1.68`). */
export const HINT_FONT_SIZE_RATIO = 1.68;

/** line-height = fontSize × this ratio (Satoshi body text rhythm). */
export const HINT_LINE_HEIGHT_RATIO = 1.2;

/** paddingX = hint-text-base × this ratio (mirrors skat.css:2113 `0.95`). */
export const HINT_PADDING_X_RATIO = 0.95;

/** paddingY = hint-text-base × this ratio (mirrors skat.css:2113 `0.50`). */
export const HINT_PADDING_Y_RATIO = 0.5;

/** Multiplayer waiting-state UX (todo/multiplayer-waiting-state-ux) — the stage machine's three
 *  clocks (static/js/waiting-state.js). */
export const WAITING = {
  // play-test: how long an applicable off-turn wait stays mute before the quiet line fades in —
  // long enough to swallow AI-turn transients and a present opponent's ordinary response time.
  // Bumped 10s -> 20s from Andre's first live 3-human prod round (2026-08-11): the anchor is
  // waitStartMs, set in waiting-state.js#startNewWait on SERVER-STATE INGEST, not on the board
  // settling — a round start's display-pause walk ends server-side and lands the state, starting
  // this clock, before the client even begins its own teardown/deal/fan animations, which run
  // several more seconds on top. So the FELT delay (from when the board visibly settles to when
  // the line appears) is shorter than this constant by that animation stretch — re-tune with that
  // gap in mind, not against the raw number.
  LINE_DELAY_MS: 20_000,
  // why: keep in sync with MultiplayerController.PRESENCE_WINDOW (5 min) — the same "acted
  // recently" recency window the /multiplayer presence dots read, so a viewer offline by the
  // board's own recency opinion is offline by the release overlay's too.
  OFFLINE_AFTER_S: 300,
  // play-test: breathing room after a wait epoch begins before the release overlay may open —
  // finishing your own move against an already-offline friend must not snap the overlay in.
  OVERLAY_GRACE_MS: 30_000,
  // why: AwaitedRecency floors idleSeconds to a whole second server-side; the client's own
  // effectiveIdleSNow() is a continuous float extrapolation from the last anchor, so a fresh
  // floored reading can land under that projection from truncation alone — with zero real
  // activity — plus whatever compute-to-anchor lag rides the snapshot that carried it (review
  // finding: truncation alone bounds under 1s; ingest lag is the harder-to-bound remainder). This
  // constant isn't chasing that lag bound tight — it doesn't need to: the release overlay is only
  // ever open once idle has already reached OFFLINE_AFTER_S (300s), so every drop that could
  // actually dismiss a shown overlay is hundreds of seconds, dwarfing any plausible tolerance
  // value by three orders of magnitude. 2s is a deliberately loose floor against truncation/lag
  // noise on a mid-wait resync, not a network-latency bound (gate-verdict tweak).
  IDLE_DROP_TOLERANCE_S: 2,
};
